import { useReactiveVar } from '@apollo/client';
import { ButtonV2, Card, Typography } from '@vartanainc/design-system';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AutoLoad from '../../components/AutoLoad';
import { HasPermission } from '../../components/HasPermission/HasPermission';
import ResetUserPasswordModal from '../../components/Modals/ResetUserPasswordModal';
import SvgIcon from '../../components/SvgIcon/SvgIcon';
import { labelValueField, ROLES } from '../../constants/common.constants';
import { ACTION, RESOURCE } from '../../constants/permissions';
import { sessionVar } from '../../graphql/cache';
import { formatCapital } from '../../utils/helpers';
import usePermissions from '../../utils/hooks/permissions';
import AssignToMyTeam from '../Settings/AssignToMyTeam';

function UserCard({
  cardTitle,
  showResetPassword,
  selectedUser,
  onEditClicked,
  isLoading,
  noSalesManagerInCompany = false,
  onAddSalesRepClicked = () => null,
}) {
  const {
    number,
    firstName,
    lastName,
    email,
    phone,
    title,
    role,
    manager,
    managedUsers,
  } = selectedUser;
  const session = useReactiveVar(sessionVar);
  const navigate = useNavigate();
  const location = useLocation();

  const [isResetUserPasswordDialogOpen, setIsResetUserPasswordDialogOpenOpen] =
    useState(false);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);

  const [, userHasPermission] = usePermissions();

  const isLoggedInUser = useCallback(() => {
    return get(session, 'session.user.email', null) === email;
  }, [session, email]);

  const handleCancel = () => {
    const params = new URLSearchParams(location.search);
    params.delete('assign');
    navigate({
      pathname: '/dashboard/settings/users',
      search: params.toString(),
    });
    setShowAssignUserModal(false);
  };

  const otherManagerUser = useMemo(() => {
    return (
      get(session, 'session.user.userRole.name', '') === ROLES.SALES_MANAGER &&
      role === ROLES.SALES &&
      manager?.id &&
      manager.id !== get(session, 'session.user.id', null)
    );
  }, [role, manager, session]);

  const isSales = useMemo(() => {
    return role === ROLES.SALES;
  }, [role]);

  const hasSalesReps = useMemo(() => {
    return role === ROLES.SALES_MANAGER && managedUsers?.length > 0;
  }, [managedUsers, role]);

  const handleViewSalesReps = () => {
    const params = new URLSearchParams(location.search);
    params.set('salesManager', selectedUser.id);
    navigate({
      pathname: '/dashboard/settings/users',
      search: params.toString(),
    });
  };

  const showViewBtn = useMemo(() => {
    const managerHasReps = role === ROLES.SALES_MANAGER && managedUsers?.length > 0;
    const sessionUser = get(session, 'session.user', null);
    const isSelfCard = managerHasReps && isLoggedInUser();
    const isAdminUser =
      [ROLES.SUPER_ADMIN].includes(sessionUser?.userRole?.name) && hasSalesReps;

    const hasViewPermission = userHasPermission('user', 'view_all');
    if ((isSelfCard || isAdminUser) && hasViewPermission) {
      return true;
    }
    return false;
  }, [
    role,
    managedUsers?.length,
    session,
    isLoggedInUser,
    hasSalesReps,
    userHasPermission,
  ]);

  const showEditCta = useMemo(() => {
    return (userHasPermission('user', 'update') || isLoggedInUser()) && !otherManagerUser;
  }, [userHasPermission, isLoggedInUser, otherManagerUser]);

  if (showAssignUserModal) {
    return <AssignToMyTeam onCancel={handleCancel} salesManager={selectedUser} />;
  }

  const showSalesManager = () => {
    if (isSales) {
      if (noSalesManagerInCompany) {
        return <></>;
      }
      return labelValueField('Sales manager', manager?.fullName || '--');
    }
    return <></>;
  };

  return (
    <Card
      variant="fullWidth"
      icon={<SvgIcon name="badge" fill="color-blue-120" className="w-8" />}
      title={(
        <div className="flex items-center justify-between w-full">
          <Typography variant="heading18" bold color="color-black-100">
            {cardTitle || '--'}
          </Typography>

          <div className="flex items-center gap-4">
            {showResetPassword && !isLoggedInUser() ? (
              <HasPermission resource="user" action="reset_password">
                <ButtonV2
                  onClick={() => setIsResetUserPasswordDialogOpenOpen(true)}
                  text="Reset password"
                  variant={{ type: 'ghost', typography: 'paragraph12' }}
                />
              </HasPermission>
            ) : null}
            {showEditCta ? (
              <ButtonV2
                onClick={onEditClicked}
                text="Edit"
                variant={{ type: 'ghost', typography: 'paragraph12' }}
              />
            ) : null}
          </div>
        </div>
      )}
      content={(
        <>
          <AutoLoad loading={isLoading}>
            <div className="flex justify-end"></div>
            <div className="grid grid-cols-2 gap-4">
              {labelValueField('First name', firstName || '--')}
              {labelValueField('Last name', lastName || '--')}
              {labelValueField('Email', email || '--')}
              {labelValueField('Phone', phone || '--')}
              {labelValueField('Job title', title || '--')}
              {!role?.toLowerCase().includes('sales') &&
                labelValueField('Role', formatCapital(role) || '--')}
            </div>
            {role?.toLowerCase().includes('sales') && (
              <div className="w-full mt-6 h-[1px] bg-vartana-steel-30" />
            )}
            <div className="mt-6 grid grid-cols-2 gap-4">
              {role?.toLowerCase().includes('sales') &&
                labelValueField('Role', formatCapital(role) || '--')}
              {role === ROLES.SALES_MANAGER && (
                <div className="flex-1">
                  <p className="break-all vartana-p-small-bold text-vartana-gray-50">
                    Sales team
                  </p>
                  <div className="flex flex-row gap-2">
                    {showViewBtn && (
                      <>
                        <button
                          className="vp-text-link-bold text-vartana-blue-60 mt-1"
                          onClick={handleViewSalesReps}
                        >
                          View
                        </button>
                        <div className="mt-1 h-[1.313rem] w-px bg-vartana-steel-30"></div>
                      </>
                    )}
                    <HasPermission resource={RESOURCE.user} action={ACTION.update}>
                      <button
                        className="vp-text-link-bold text-vartana-blue-60 mt-1"
                        onClick={() => {
                          onAddSalesRepClicked();
                          setShowAssignUserModal(true);
                        }}
                      >
                        Add sales rep
                      </button>
                    </HasPermission>
                  </div>
                </div>
              )}
              {showSalesManager()}
            </div>
          </AutoLoad>
          <ResetUserPasswordModal
            open={isResetUserPasswordDialogOpen}
            selectedUser={{
              email,
              number,
            }}
            handleClose={() => setIsResetUserPasswordDialogOpenOpen(false)}
          />
        </>
      )}
    >
    </Card>
  );
}

UserCard.propTypes = {
  noSalesManagerInCompany: PropTypes.bool,
  cardTitle: PropTypes.string.isRequired,
  showResetPassword: PropTypes.bool,
  selectedUser: PropTypes.shape({
    id: PropTypes.string,
    number: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    title: PropTypes.string,
    phone: PropTypes.string,
    role: PropTypes.string,
    manager: PropTypes.shape({
      fullName: PropTypes.string,
      id: PropTypes.string,
    }),
    managedUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      })
    ),
  }),
  onEditClicked: PropTypes.func,
  onAddSalesRepClicked: PropTypes.func,
  isLoading: PropTypes.bool,
};

UserCard.defaultProps = {
  selectedUser: {},
  showResetPassword: false,
  noSalesManagerInCompany: false,
  onEditClicked: () => {},
  onAddSalesRepClicked: () => {},
  isLoading: false,
};

export default UserCard;
